
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

interface FavoriteItem {
  id: string;
  item_type: string;
  item_id: string;
  item_title: string;
  item_image: string;
  item_data: any;
  created_at: string;
}

export function useFavorites() {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadFavorites();
    } else {
      setFavorites([]);
      setLoading(false);
    }
  }, [user]);

  const loadFavorites = async () => {
    try {
      const { data, error } = await supabase
        .from('favorites')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setFavorites(data || []);
    } catch (error) {
      console.error('Error loading favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const isFavorite = (itemId: string) => {
    return favorites.some((fav) => fav.item_id === itemId);
  };

  const addFavorite = async (
    itemType: string,
    itemId: string,
    itemTitle: string,
    itemImage: string,
    itemData?: any
  ) => {
    if (!user) return false;

    try {
      const { error } = await supabase.from('favorites').insert([
        {
          user_id: user.id,
          item_type: itemType,
          item_id: itemId,
          item_title: itemTitle,
          item_image: itemImage,
          item_data: itemData || {},
        },
      ]);

      if (error) throw error;
      await loadFavorites();
      return true;
    } catch (error) {
      console.error('Error adding favorite:', error);
      return false;
    }
  };

  const removeFavorite = async (itemId: string) => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('favorites')
        .delete()
        .eq('user_id', user.id)
        .eq('item_id', itemId);

      if (error) throw error;
      await loadFavorites();
      return true;
    } catch (error) {
      console.error('Error removing favorite:', error);
      return false;
    }
  };

  const toggleFavorite = async (
    itemType: string,
    itemId: string,
    itemTitle: string,
    itemImage: string,
    itemData?: any
  ) => {
    if (isFavorite(itemId)) {
      return await removeFavorite(itemId);
    } else {
      return await addFavorite(itemType, itemId, itemTitle, itemImage, itemData);
    }
  };

  return {
    favorites,
    loading,
    isFavorite,
    addFavorite,
    removeFavorite,
    toggleFavorite,
  };
}
